/*
 * Helpers for routing audio to a specific output device (sink).
 * Uses HTMLMediaElement.setSinkId which is supported in Chrome, Edge and Firefox 116+.
 * Safari does not support it so everything falls back to the system default output.
 */

// Sentinel for the browser/system default output
export const DEFAULT_SINK = 'default';

export const isSinkSelectionSupported = () =>
  typeof HTMLMediaElement !== 'undefined' &&
  'setSinkId' in HTMLMediaElement.prototype;

// Route an audio element to a sink. Falls back to default if the device is gone.
export async function applySink(el, sinkId) {
  if (!el || !isSinkSelectionSupported()) {
    return;
  }
  // '' is the default output in every browser, 'default' only exists in Chromium
  const id = !sinkId || sinkId == DEFAULT_SINK ? '' : sinkId;
  if (el.sinkId === id) {
    return;
  }
  try {
    await el.setSinkId(id);
  } catch (e) {
    console.warn('Unable to set audio output, using default', sinkId, e);
    try {
      await el.setSinkId('');
    } catch (e2) {
      // Nothing else we can do here
    }
  }
}

// Stop and unload an element without firing a bogus src error
function releaseAudio(el) {
  el.pause();
  el.removeAttribute('src');
  el.load();
}

/*
 * Plays the same sound on one or more output devices at once.
 * Mirrors the parts of the Audio API we use: play, pause, currentTime, volume.
 */
export class MultiSinkAudio {
  constructor(url, sinkIds = [DEFAULT_SINK]) {
    this.url = url;
    this.elements = [];
    this.ready = Promise.resolve();
    this.setSinks(sinkIds);
  }

  // Update the outputs. Can be called while playing.
  setSinks(sinkIds) {
    // Only allow one element per sink otherwise we double up on the same device
    const ids = [...new Set(sinkIds?.length ? sinkIds : [DEFAULT_SINK])];
    const playing = this.elements.some((el) => !el.paused);
    const currentTime = this.elements[0]?.currentTime ?? 0;
    const volume = this.elements[0]?.volume ?? 1;

    // Reuse the elements we have, add or drop the rest
    while (this.elements.length > ids.length) {
      releaseAudio(this.elements.pop());
    }
    while (this.elements.length < ids.length) {
      const el = new Audio(this.url);
      el.volume = volume;
      this.elements.push(el);
    }
    this.ready = Promise.all(
      this.elements.map((el, i) => applySink(el, ids[i]))
    );
    if (playing) {
      this.play();
    } else {
      this.elements.forEach((el) => (el.currentTime = currentTime));
    }
  }

  async play() {
    await this.ready;
    return Promise.all(
      this.elements.map((el) =>
        el.play().catch((e) => console.warn('Unable to play audio', e))
      )
    );
  }

  pause() {
    this.elements.forEach((el) => el.pause());
  }

  get paused() {
    return this.elements.every((el) => el.paused);
  }

  get currentTime() {
    return this.elements[0]?.currentTime ?? 0;
  }

  set currentTime(t) {
    this.elements.forEach((el) => (el.currentTime = t));
  }

  get volume() {
    return this.elements[0]?.volume ?? 1;
  }

  set volume(v) {
    this.elements.forEach((el) => (el.volume = v));
  }

  destroy() {
    this.elements.forEach(releaseAudio);
    this.elements = [];
  }
}
